import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generateId, countWords } from './utils';

// saveNovel import removed

export type BubbleType = 'character' | 'location' | 'item' | 'plot' | 'research' | 'note';
export type ChapterStatus = 'brainstorm' | 'drafting' | 'polished';
export type SceneStatus = 'brainstorm' | 'drafting' | 'polished';
export type ViewMode = 'manuscript' | 'constellation' | 'timeline' | 'compost' | 'dashboard' | 'corkboard' | 'dictionary' | 'stats' | 'studio' | 'admin' | 'challenges';
export type SidebarMode = 'hover' | 'pinned';
export type TimelineMode = 'acts' | 'kanban';
export type Theme = 'light' | 'dark' | 'system';

export type WorkType = 'fiction' | 'nonfiction' | 'poetry' | 'screenwriting' | 'journal';

export const WORK_TYPE_OPTIONS: { id: WorkType; label: string; description: string; glyph: string }[] = [
  { id: 'fiction', label: 'Fiction', description: 'Novels, short stories, speculative worlds', glyph: '✦' },
  { id: 'nonfiction', label: 'Non-fiction', description: 'Memoir, essays, journalism, biography', glyph: '◈' },
  { id: 'poetry', label: 'Poetry', description: 'Verse, lyric essays, prose poetry', glyph: '◇' },
  { id: 'screenwriting', label: 'Screenwriting', description: 'Film, TV, stage, and new media', glyph: '▷' },
  { id: 'journal', label: 'Journal', description: 'Personal reflection, morning pages', glyph: '○' },
];

// ============================================================================
// LICENSE SYSTEM (One-time purchase)
// ============================================================================
export interface LicenseState {
  isLicensed: boolean;
  licenseKey: string | null;
}

// ============================================================================
// THEME SYSTEM
// ============================================================================
export type ThemePreset = 'cream' | 'sepia' | 'moonlit' | 'paperwhite' | 'forest' | 'midnight' | 'highcontrast';

export interface ThemeProfile {
  preset: ThemePreset | 'custom';
  background: string;
  paperSurface: string;
  text: string;
  accent: string;
  border: string;
  manuscriptFont: string;
  manuscriptSize: number;
  lineHeight: number;
  letterSpacing: number;
  bookTexture: boolean;
}

export const themePresets: Record<ThemePreset, Omit<ThemeProfile, 'preset' | 'manuscriptFont' | 'manuscriptSize' | 'lineHeight' | 'letterSpacing' | 'bookTexture'>> = {
  cream: {
    background: '#F5F1E8',
    paperSurface: '#FAF7F0',
    text: '#1E1A12',
    accent: '#8B7355',
    border: 'rgba(30, 26, 18, 0.22)',
  },
  sepia: {
    background: '#E8DCC4',
    paperSurface: '#F0E6D2',
    text: '#2B2317',
    accent: '#6B5B45',
    border: 'rgba(43, 35, 23, 0.24)',
  },
  moonlit: {
    background: '#1A1A2E',
    paperSurface: '#252542',
    text: '#E8E8F0',
    accent: '#7B8CDE',
    border: 'rgba(232, 232, 240, 0.1)',
  },
  paperwhite: {
    background: '#FFFFFF',
    paperSurface: '#FAFAFA',
    text: '#1A1A1A',
    accent: '#4A5568',
    border: 'rgba(0, 0, 0, 0.08)',
  },
  forest: {
    background: '#1C2621',
    paperSurface: '#243029',
    text: '#E0E8E3',
    accent: '#7A9E7E',
    border: 'rgba(224, 232, 227, 0.1)',
  },
  midnight: {
    background: '#0D1117',
    paperSurface: '#161B22',
    text: '#C9D1D9',
    accent: '#58A6FF',
    border: 'rgba(201, 209, 217, 0.1)',
  },
  highcontrast: {
    background: '#FFFFFF',
    paperSurface: '#FFFFFF',
    text: '#000000',
    accent: '#005fcc',
    border: 'rgba(0, 0, 0, 0.5)',
  },
};

export const defaultThemeProfile: ThemeProfile = {
  preset: 'cream',
  background: '#F5F1E8',
  paperSurface: '#FAF7F0',
  text: '#2C2416',
  accent: '#8B7355',
  border: 'rgba(44, 36, 22, 0.12)',
  manuscriptFont: '"Crimson Text", Georgia, serif',
  manuscriptSize: 18,
  lineHeight: 1.8,
  letterSpacing: 0.01,
  bookTexture: false,
};

// ============================================================================
// SETTINGS
// ============================================================================
export interface AppSettings {
  quietMode: boolean;
  spellcheckEnabled: boolean;
  grammarEnabled: boolean;
  autoBackupEnabled: boolean;
  autoBackupInterval: number;
  versionHistoryEnabled: boolean;
  dailyTimeGoal: number | null;
  dailyWordGoal: number | null;
  streakEnabled: boolean;
  nextSentenceNudge: boolean;
  momentumMeterEnabled: boolean;
  microDelightsEnabled: boolean;
  breadcrumbsEnabled: boolean;
  typewriterSoundsEnabled: boolean;
  typewriterScrollingEnabled: boolean;
  smartTypographyEnabled: boolean;
  wordCountArcEnabled: boolean;
  fixedToolbarEnabled: boolean;
  floatingToolbarEnabled: boolean;
  marginMenuEnabled: boolean;
  soundProfile: 'mechanical' | 'typewriter';
}

export const defaultSettings: AppSettings = {
  quietMode: false,
  spellcheckEnabled: true,
  grammarEnabled: true,
  autoBackupEnabled: true,
  autoBackupInterval: 10,
  versionHistoryEnabled: true,
  dailyTimeGoal: null,
  dailyWordGoal: 1000,
  streakEnabled: true,
  nextSentenceNudge: true,
  momentumMeterEnabled: true,
  microDelightsEnabled: true,
  breadcrumbsEnabled: true,
  typewriterSoundsEnabled: true,
  typewriterScrollingEnabled: true,
  smartTypographyEnabled: true,
  wordCountArcEnabled: false,
  fixedToolbarEnabled: true,
  floatingToolbarEnabled: false,
  marginMenuEnabled: false,
  soundProfile: 'mechanical',
};

// ============================================================================
// BACKUP / VERSION HISTORY
// ============================================================================
export interface BackupEntry {
  id: string;
  timestamp: string;
  novelId: string;
  novelSnapshot: Novel;
}

export interface VersionSnapshot {
  id: string;
  timestamp: string;
  chapterId: string;
  content: string;
  wordCount: number;
}

// ============================================================================
// PSYCHOLOGY / HABIT FEATURES
// ============================================================================
export interface WritingSession {
  id: string;
  startTime: string;
  endTime: string | null;
  duration: number;
  wordCount: number;
}

export interface StreakData {
  currentStreak: number;
  lastWritingDate: string | null;
  totalDaysWritten: number;
  isPaused: boolean;
}

export interface FocusSession {
  isActive: boolean;
  startTime: string | null;
  duration: number;
  timeRemaining: number;
}

export interface MomentumData {
  level: number;
  lastTypingTime: string | null;
}

// ============================================================================
// AUTHOR'S DICTIONARY
// ============================================================================
export interface CustomWord {
  word: string;
  definition: string;
}

export interface StickyNote {
  id: string;
  content: string;
  x: number;
  y: number;
  color: string;
  rotation: number;
}

export interface StickyLink {
  id: string;
  fromId: string;
  toId: string;
}

// ============================================================================
// CORE TYPES
// ============================================================================
export interface Bubble {
  id: string;
  type: BubbleType;
  name: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  x: number;
  y: number;
  connections: string[];
}

export interface Chapter {
  id: string;
  title: string;
  content: string;
  wordCount: number;
  status: ChapterStatus;
  order: number;
  createdAt: string;
  updatedAt: string;
  color?: string;
  breadcrumb?: string;
}

export interface Scene {
  id: string;
  chapterId: string;
  title: string;
  description: string;
  duration: number;
  status: SceneStatus;
  act: 1 | 2 | 3;
  order: number;
  linkedBubbles: string[];
}

export interface CompostItem {
  id: string;
  content: string;
  chapterId: string;
  chapterTitle: string;
  deletedAt: string;
  wordCount: number;
}

export interface TextLink {
  id: string;
  text: string;
  chapterId: string;
  bubbleId: string;
  startIndex: number;
  endIndex: number;
}

export interface ChapterImage {
  id: string;
  chapterId: string;
  dataUrl: string;
  caption: string;
  order: number;
}

export interface Novel {
  id: string;
  title: string;
  description: string;
  coverColor: string;
  coverImage: string | null;
  bookTexture: string | null;
  createdAt: string;
  updatedAt: string;
  chapters: Chapter[];
  bubbles: Bubble[];
  scenes: Scene[];
  textLinks: TextLink[];
  compost: CompostItem[];
  chapterImages: ChapterImage[];
  activeChapterId: string | null;
  targetWordCount: number;
  customWords: CustomWord[];
  stickyNotes: StickyNote[];
  stickyLinks: StickyLink[];
  cloudSyncVersion?: number; // Track cloud version for conflict detection
  workType?: WorkType; // Type of writing project
}

// ============================================================================
// APP STATE INTERFACE
// ============================================================================
interface AppState {
  license: LicenseState;
  themeProfile: ThemeProfile;
  previousThemePreset: ThemePreset | 'custom' | null;
  hasCompletedOnboarding: boolean;
  hasSeenConstellationTour: boolean;
  settings: AppSettings;
  sidebarMode: SidebarMode;
  viewMode: ViewMode;
  showBubblePanel: boolean;
  timelineMode: TimelineMode;
  showSettings: boolean;
  theme: Theme;
  novels: Novel[];
  activeNovelId: string | null;
  selectedBubbleId: string | null;
  hoveredBubbleId: string | null;
  constellationZoom: number;
  constellationOffset: { x: number; y: number };
  backups: BackupEntry[];
  lastBackupTime: string | null;
  versionSnapshots: VersionSnapshot[];
  todayWritingTime: number;
  sessions: WritingSession[];
  streak: StreakData;
  focusSession: FocusSession;
  momentum: MomentumData;
  lastWarmStart: string | null;
  draggedBubbleId: string | null;
  /** ISO-date string → words written that day (across all novels) */
  dailyWordCounts: Record<string, number>;
  isSyncing: boolean;
  syncError: string | null;
  lastSyncFailed: boolean;
  clearSyncError: () => void;

  // Actions
  loadNovelsFromCloud: () => Promise<void>;
  syncNovelToCloud: (novelId: string) => Promise<void>;
  deleteNovelFromCloud: (novelId: string) => Promise<void>;
  activateLicense: (key: string) => void;
  // Author's Dictionary
  addCustomWord: (novelId: string, word: string, definition: string) => void;
  removeCustomWord: (novelId: string, word: string) => void;
  updateCustomWord: (novelId: string, word: string, definition: string) => void;

  // Sticky Note Actions
  addStickyNote: (novelId: string, x: number, y: number) => void;
  updateStickyNote: (novelId: string, noteId: string, updates: Partial<StickyNote>) => void;
  deleteStickyNote: (novelId: string, noteId: string) => void;
  addStickyLink: (novelId: string, fromId: string, toId: string) => void;
  removeStickyLink: (novelId: string, linkId: string) => void;

  setThemeProfile: (profile: Partial<ThemeProfile>) => void;
  setThemePreset: (preset: ThemePreset) => void;
  completeOnboarding: () => void;
  completeConstellationTour: () => void;
  applyThemeToCSS: () => void;
  updateSettings: (settings: Partial<AppSettings>) => void;
  toggleQuietMode: () => void;
  toggleSetting: (key: keyof AppSettings) => void;
  setSidebarMode: (mode: SidebarMode) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleBubblePanel: () => void;
  setTimelineMode: (mode: TimelineMode) => void;
  setShowSettings: (show: boolean) => void;
  setTheme: (theme: Theme) => void;
  createNovel: (title: string, description?: string, workType?: WorkType) => string;
  setActiveNovel: (id: string) => void;
  updateNovel: (id: string, updates: Partial<Novel>) => void;
  deleteNovel: (id: string) => void;
  importNovel: (novel: Novel) => void;
  getNovelStats: (novelId: string) => { totalWords: number; totalChapters: number; totalBubbles: number; progress: number };
  setActiveChapter: (id: string) => void;
  createChapter: (title?: string) => void;
  updateChapter: (id: string, updates: Partial<Chapter>) => void;
  updateChapterContent: (id: string, content: string) => void;
  deleteChapter: (id: string) => void;
  reorderChapters: (chapterIds: string[]) => void;
  updateChapterTitle: (chapterId: string, title: string) => void;
  setChapterColor: (chapterId: string, color: string) => void;
  setNovelCoverColor: (novelId: string, color: string) => void;
  createBubble: (type: BubbleType, name: string, content?: string) => string;
  updateBubble: (id: string, updates: Partial<Bubble>) => void;
  deleteBubble: (id: string) => void;
  moveBubble: (id: string, x: number, y: number) => void;
  setSelectedBubble: (id: string | null) => void;
  setHoveredBubble: (id: string | null) => void;
  setDraggedBubble: (id: string | null) => void;
  connectBubbles: (fromId: string, toId: string) => void;
  disconnectBubbles: (fromId: string, toId: string) => void;
  getConnectedBubbles: (bubbleId: string) => string[];
  getBubbleConnectionCount: (bubbleId: string) => number;
  setConstellationZoom: (zoom: number) => void;
  setConstellationOffset: (offset: { x: number; y: number }) => void;
  resetConstellationView: () => void;
  createTextLink: (text: string, chapterId: string, bubbleId: string, startIndex: number, endIndex: number) => void;
  deleteTextLink: (id: string) => void;
  getLinksForChapter: (chapterId: string) => TextLink[];
  createScene: (scene: Omit<Scene, 'id'>) => void;
  updateScene: (id: string, updates: Partial<Scene>) => void;
  moveScene: (sceneId: string, targetAct: 1 | 2 | 3, targetOrder: number) => void;
  moveSceneToKanban: (sceneId: string, newStatus: SceneStatus) => void;
  deleteScene: (id: string) => void;
  addToCompost: (content: string, chapterId: string, chapterTitle: string) => void;
  restoreFromCompost: (id: string) => void;
  deleteFromCompost: (id: string) => void;
  createBackup: () => void;
  restoreBackup: (id: string) => void;
  deleteBackup: (id: string) => void;
  createSnapshot: (chapterId: string) => void;
  restoreSnapshot: (id: string) => void;
  getSnapshotsForChapter: (chapterId: string) => VersionSnapshot[];
  addChapterImage: (chapterId: string, dataUrl: string, caption?: string) => void;
  deleteChapterImage: (id: string) => void;
  reorderChapterImages: (chapterId: string, imageIds: string[]) => void;
  startWritingSession: () => void;
  endWritingSession: () => void;
  incrementWritingTime: (seconds: number) => void;
  updateStreak: () => void;
  startFocusSession: (minutes: number) => void;
  endFocusSession: () => void;
  updateFocusTimeRemaining: (seconds: number) => void;
  updateMomentum: (level: number) => void;
  setLastWarmStart: (timestamp: string) => void;
  recordDailyWords: (wordsAdded: number) => void;
  exportToMarkdown: () => string;
  exportToTxt: () => string;
  exportChapterToTxt: (chapterId: string) => string;
}

// generateId and countWords now imported from utils.ts
export const coverColors = ['#5A7A7A', '#6B8E7A', '#7A8B9A', '#8A7A9A', '#7A9A8A', '#8A8A8A', '#9A7A7A', '#7A7A9A'];

const createDefaultNovel = (): Novel => ({
  id: generateId(),
  title: 'My First Novel',
  description: 'A new story waiting to be told',
  coverColor: coverColors[0],
  coverImage: null,
  bookTexture: null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  targetWordCount: 50000,
  chapters: [
    {
      id: 'ch-1',
      title: 'Chapter 1: The Beginning',
      content: `The old castle stood silent on the hill, its stones weathered by centuries of wind and rain. Marcus approached the gate with caution, his hand resting on the hilt of the dagger he had found in his grandfather's attic.

The blade seemed to hum with an energy he couldn't explain, as if it remembered battles fought long before his birth. He looked up at the castle walls, wondering what secrets lay within.

The gate creaked open as if expecting him.`,
      wordCount: 82,
      status: 'drafting',
      order: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'ch-2',
      title: 'Chapter 2: The Discovery',
      content: `Inside the castle walls, Marcus found a library untouched by time. Books lined shelves that stretched to the vaulted ceiling, their spines leather-bound and gilded.

One volume in particular caught his eye—a chronicle of the blade's history, detailing its journey through generations of guardians.`,
      wordCount: 42,
      status: 'brainstorm',
      order: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  activeChapterId: 'ch-1',
  bubbles: [
    { id: 'bubble-1', type: 'character', name: 'Marcus', content: 'Protagonist. Young historian.', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), x: 25, y: 35, connections: [] },
    { id: 'bubble-2', type: 'location', name: 'The Old Castle', content: 'Ancient fortress.', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), x: 65, y: 25, connections: [] },
    { id: 'bubble-3', type: 'item', name: 'The Dagger', content: 'Ancient blade.', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), x: 45, y: 60, connections: [] },
  ],
  scenes: [
    { id: 'scene-1', chapterId: 'ch-1', title: 'Approaching the Castle', description: 'Marcus arrives', duration: 30, status: 'drafting', act: 1, order: 0, linkedBubbles: ['bubble-1', 'bubble-2'] },
    { id: 'scene-2', chapterId: 'ch-1', title: 'The Dagger\'s Reaction', description: 'Blade hums', duration: 15, status: 'drafting', act: 1, order: 1, linkedBubbles: ['bubble-3'] },
    { id: 'scene-3', chapterId: 'ch-2', title: 'The Hidden Library', description: 'Secret library', duration: 45, status: 'brainstorm', act: 1, order: 2, linkedBubbles: ['bubble-2'] },
  ],
  textLinks: [],
  compost: [],
  chapterImages: [],
  customWords: [],
  stickyNotes: [],
  stickyLinks: [],
});

export const useAppStore = create<AppState>()(
  persist<AppState>(

    (set, get) => ({
      license: { isLicensed: false, licenseKey: null },
      themeProfile: defaultThemeProfile,
      previousThemePreset: null,
      hasCompletedOnboarding: false,
      hasSeenConstellationTour: false,
      settings: defaultSettings,
      sidebarMode: 'pinned',
      viewMode: 'dashboard',
      showBubblePanel: true,
      timelineMode: 'acts',
      showSettings: false,
      theme: 'system',
      novels: [createDefaultNovel()],
      activeNovelId: null,
      selectedBubbleId: null,
      hoveredBubbleId: null,
      constellationZoom: 1,
      constellationOffset: { x: 0, y: 0 },
      backups: [],
      lastBackupTime: null,
      versionSnapshots: [],
      todayWritingTime: 0,
      sessions: [],
      streak: { currentStreak: 0, lastWritingDate: null, totalDaysWritten: 0, isPaused: false },
      focusSession: { isActive: false, startTime: null, duration: 0, timeRemaining: 0 },
      momentum: { level: 0, lastTypingTime: null },
      lastWarmStart: null,
      draggedBubbleId: null,
      dailyWordCounts: {},
      isSyncing: false,
      syncError: null,
      lastSyncFailed: false,

      loadNovelsFromCloud: async () => {
        set({ isSyncing: true });
        try {
          const API_URL = import.meta.env.VITE_API_URL || 'https://antigravity-api.gutlessbook.workers.dev';

          const response = await fetch(`${API_URL}/api/novels`, {
            credentials: 'include'
          });

          if (!response.ok) {
            if (response.status === 401) {
              console.log('[Store] User not authenticated, using local novels only');
              set({ isSyncing: false });
              return;
            }
            throw new Error(`HTTP ${response.status}`);
          }

          const data = await response.json();
          const cloudNovels: Novel[] = data.novels || [];

          if (cloudNovels.length > 0) {
            set((state) => {
              // Merge cloud novels with local novels
              // Cloud takes precedence for existing IDs, new ones are added
              const mergedMap = new Map(state.novels.map(n => [n.id, n]));
              cloudNovels.forEach(n => {
                // Only overwrite if cloud version is newer or local doesn't exist
                const existing = mergedMap.get(n.id);
                if (!existing || (n as any).updatedAt > (existing as any).updatedAt) {
                  mergedMap.set(n.id, n);
                }
              });

              const mergedNovels = Array.from(mergedMap.values());
              return {
                novels: mergedNovels,
                activeNovelId: state.activeNovelId || mergedNovels[0]?.id || null,
                isSyncing: false
              };
            });
            console.log(`[Store] Loaded ${cloudNovels.length} novels from cloud`);
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.error('Failed to load novels from cloud:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to sync your novels';
          set({
            isSyncing: false,
            syncError: errorMessage,
            lastSyncFailed: true
          });
        }
      },

      clearSyncError: () => set({ syncError: null, lastSyncFailed: false }),

      syncNovelToCloud: async (novelId) => {
        const novel = get().novels.find(n => n.id === novelId);
        if (!novel) return;
        const API_URL = import.meta.env.VITE_API_URL || 'https://antigravity-api.gutlessbook.workers.dev';
        set({ isSyncing: true });
        try {
          const res = await fetch(`${API_URL}/api/novels`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ novel }),
          });
          if (res.status === 401) { set({ isSyncing: false }); return; }
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (data.version) {
            set(state => ({
              novels: state.novels.map(n => n.id === novelId ? { ...n, cloudSyncVersion: data.version } : n),
              isSyncing: false,
            }));
          } else {
            set({ isSyncing: false });
          }
        } catch (err) {
          console.error('[Store] syncNovelToCloud error:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to save to cloud';
          set({
            isSyncing: false,
            syncError: errorMessage,
            lastSyncFailed: true
          });
        }
      },

      deleteNovelFromCloud: async (novelId) => {
        const API_URL = import.meta.env.VITE_API_URL || 'https://antigravity-api.gutlessbook.workers.dev';
        try {
          await fetch(`${API_URL}/api/novels/${novelId}`, { method: 'DELETE', credentials: 'include' });
        } catch (err) {
          console.error('[Store] deleteNovelFromCloud error:', err);
        }
      },

      activateLicense: (key) => set({ license: { isLicensed: true, licenseKey: key } }),

      setThemeProfile: (profile) => set(state => ({ themeProfile: { ...state.themeProfile, ...profile } })),
      setThemePreset: (preset) => {
        const p = themePresets[preset];
        set(state => ({ themeProfile: { ...state.themeProfile, preset, background: p.background, paperSurface: p.paperSurface, text: p.text, accent: p.accent, border: p.border } }));
      },
      completeOnboarding: () => set({ hasCompletedOnboarding: true }),
      completeConstellationTour: () => set({ hasSeenConstellationTour: true }),
      applyThemeToCSS: () => {
        const { themeProfile } = get();
        const root = document.documentElement;

        // Set studio variables
        root.style.setProperty('--studio-bg', themeProfile.background);
        root.style.setProperty('--studio-paper', themeProfile.paperSurface);
        root.style.setProperty('--studio-text', themeProfile.text);
        root.style.setProperty('--studio-accent', themeProfile.accent);
        root.style.setProperty('--studio-border', themeProfile.border);
        root.style.setProperty('--studio-font', themeProfile.manuscriptFont);
        root.style.setProperty('--studio-size', `${themeProfile.manuscriptSize}px`);
        root.style.setProperty('--studio-line-height', `${themeProfile.lineHeight}`);
        root.style.setProperty('--studio-letter-spacing', `${themeProfile.letterSpacing}em`);
      },

      addCustomWord: (novelId, word, definition) => {
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            customWords: [...(n.customWords || []).filter(cw => cw.word.toLowerCase() !== word.toLowerCase()), { word, definition }],
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },
      removeCustomWord: (novelId, word) => {
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            customWords: (n.customWords || []).filter(cw => cw.word.toLowerCase() !== word.toLowerCase()),
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },
      updateCustomWord: (novelId, word, definition) => {
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            customWords: (n.customWords || []).map(cw => cw.word.toLowerCase() === word.toLowerCase() ? { word, definition } : cw),
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },

      addStickyNote: (novelId, x, y) => {
        const newNote: StickyNote = {
          id: Math.random().toString(36).substr(2, 9),
          content: '',
          x,
          y,
          color: ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#ddd6fe'][Math.floor(Math.random() * 5)],
          rotation: Math.random() * 6 - 3
        };
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            stickyNotes: [...(n.stickyNotes || []), newNote],
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },

      updateStickyNote: (novelId, noteId, updates) => {
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            stickyNotes: (n.stickyNotes || []).map(sn => sn.id === noteId ? { ...sn, ...updates } : sn),
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },

      deleteStickyNote: (novelId, noteId) => {
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            stickyNotes: (n.stickyNotes || []).filter(sn => sn.id !== noteId),
            stickyLinks: (n.stickyLinks || []).filter(sl => sl.fromId !== noteId && sl.toId !== noteId),
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },

      addStickyLink: (novelId, fromId, toId) => {
        const newLink: StickyLink = {
          id: `${fromId}-${toId}`,
          fromId,
          toId
        };
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            stickyLinks: [...(n.stickyLinks || []).filter(l => l.id !== newLink.id), newLink],
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },

      removeStickyLink: (novelId, linkId) => {
        set(state => ({
          novels: state.novels.map(n => n.id === novelId ? {
            ...n,
            stickyLinks: (n.stickyLinks || []).filter(sl => sl.id !== linkId),
            updatedAt: new Date().toISOString()
          } : n)
        }));
      },

      applyComponentTheme: () => {
        const { themeProfile } = get();
        const root = document.documentElement;
        root.style.setProperty('--bg-primary', themeProfile.background);
        root.style.setProperty('--bg-secondary', themeProfile.paperSurface);
        root.style.setProperty('--bg-tertiary', themeProfile.background);
        root.style.setProperty('--bg-card', themeProfile.paperSurface);
        root.style.setProperty('--bg-hover', themeProfile.border);

        root.style.setProperty('--text-primary', themeProfile.text);
        root.style.setProperty('--text-secondary', themeProfile.accent);
        root.style.setProperty('--text-muted', themeProfile.accent + '99');

        root.style.setProperty('--border-light', themeProfile.border);
        root.style.setProperty('--border-soft', themeProfile.border);
        root.style.setProperty('--border-muted', themeProfile.border);

        root.style.setProperty('--sage', themeProfile.accent);
        root.style.setProperty('--sage-soft', themeProfile.accent);
        root.style.setProperty('--sage-light', themeProfile.accent + '40');
      },

      updateSettings: (newSettings) => set(state => ({ settings: { ...state.settings, ...newSettings } })),
      toggleQuietMode: () => set(state => ({ settings: { ...state.settings, quietMode: !state.settings.quietMode } })),
      toggleSetting: (key) => set(state => ({ settings: { ...state.settings, [key]: !state.settings[key] } })),

      setSidebarMode: (mode) => set({ sidebarMode: mode }),
      setViewMode: (mode) => set({ viewMode: mode, showBubblePanel: mode === 'constellation' }),
      toggleBubblePanel: () => set(state => ({ showBubblePanel: !state.showBubblePanel })),
      setTimelineMode: (mode) => set({ timelineMode: mode }),
      setShowSettings: (show) => set({ showSettings: show }),
      setTheme: (theme) => set(state => {
        const profile = { ...state.themeProfile };

        // This acts as a "paperwhite toggle". If they want 'light' mode (paperwhite), 
        // save the current preset to previousThemePreset and switch to paperwhite.
        if (theme === 'light') {
          return {
            theme: 'light',
            previousThemePreset: profile.preset,
            themeProfile: { ...profile, preset: 'paperwhite', ...themePresets.paperwhite }
          };
        }

        // If they want 'dark' mode (reverting), restore the previous theme preset if it exists,
        // otherwise default to moonlit.
        if (theme === 'dark') {
          const revertPreset = state.previousThemePreset || 'moonlit';
          if (revertPreset === 'custom') {
            // Can't automatically revert to custom without tracking custom colors, so default to moonlit
            return {
              theme: 'dark',
              previousThemePreset: null,
              themeProfile: { ...profile, preset: 'moonlit', ...themePresets.moonlit }
            };
          }
          const presetObj = themePresets[revertPreset];
          return {
            theme: 'dark',
            previousThemePreset: null,
            themeProfile: { ...profile, preset: revertPreset, ...presetObj }
          };
        }

        return { theme };
      }),

      createNovel: (title, description = '') => {
        const newNovel: Novel = {
          id: generateId(),
          title,
          description,
          coverColor: coverColors[Math.floor(Math.random() * coverColors.length)],
          coverImage: null,
          bookTexture: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          targetWordCount: 50000,
          chapters: [],
          bubbles: [],
          scenes: [],
          textLinks: [],
          compost: [],
          chapterImages: [],
          activeChapterId: null,
          customWords: [],
          stickyNotes: [],
          stickyLinks: [],
        };
        set(state => ({ novels: [...state.novels, newNovel], activeNovelId: newNovel.id, viewMode: 'manuscript', showBubblePanel: false }));
        return newNovel.id;
      },
      setActiveNovel: (id) => set({ activeNovelId: id, viewMode: 'manuscript', showBubblePanel: false, selectedBubbleId: null, constellationZoom: 1, constellationOffset: { x: 0, y: 0 } }),
      updateNovel: (id, updates) => set(state => ({ novels: state.novels.map(n => n.id === id ? { ...n, ...updates, updatedAt: new Date().toISOString() } : n) })),
      deleteNovel: (id) => {
        const remaining = get().novels.filter(n => n.id !== id);
        set({ novels: remaining, activeNovelId: remaining.length > 0 ? remaining[0].id : null, viewMode: remaining.length > 0 ? 'manuscript' : 'dashboard', showBubblePanel: false });
        // Cloud delete — non-blocking, fire and forget
        const API_URL = import.meta.env.VITE_API_URL || 'https://antigravity-api.gutlessbook.workers.dev';
        fetch(`${API_URL}/api/novels/${id}`, { method: 'DELETE', credentials: 'include' })
          .catch(err => console.error('[Store] Cloud delete failed:', err));
      },
      importNovel: (novel) => {
        // Ensure we don't duplicate — if a novel with the same ID exists, update it;
        // otherwise add as a new entry.
        const existing = get().novels.find(n => n.id === novel.id);
        if (existing) {
          set(state => ({
            novels: state.novels.map(n => n.id === novel.id ? novel : n),
            activeNovelId: novel.id,
            viewMode: 'manuscript',
          }));
        } else {
          set(state => ({
            novels: [...state.novels, novel],
            activeNovelId: novel.id,
            viewMode: 'manuscript',
          }));
        }
      },
      getNovelStats: (novelId) => {
        const novel = get().novels.find(n => n.id === novelId);
        if (!novel) return { totalWords: 0, totalChapters: 0, totalBubbles: 0, progress: 0 };
        const totalWords = novel.chapters.reduce((sum, ch) => sum + ch.wordCount, 0);
        return { totalWords, totalChapters: novel.chapters.length, totalBubbles: novel.bubbles.length, progress: novel.targetWordCount > 0 ? Math.min(100, Math.round((totalWords / novel.targetWordCount) * 100)) : 0 };
      },

      setActiveChapter: (id) => {
        const { activeNovelId } = get();
        if (!activeNovelId) return;
        set(state => ({
          novels: state.novels.map(n => n.id === activeNovelId ? { ...n, activeChapterId: id } : n)
        }));
      },
      createChapter: (title) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const newChapter: Chapter = { id: generateId(), title: title || `Chapter ${novel.chapters.length + 1}`, content: '', wordCount: 0, status: 'brainstorm', order: novel.chapters.length, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: [...n.chapters, newChapter], activeChapterId: newChapter.id } : n) });
      },
      updateChapter: (id, updates) => {
        const { activeNovelId } = get();
        if (!activeNovelId) return;
        set(state => ({
          novels: state.novels.map(n => n.id === activeNovelId ? {
            ...n,
            chapters: n.chapters.map(ch => ch.id === id ? { ...ch, ...updates, updatedAt: new Date().toISOString() } : ch)
          } : n)
        }));
      },
      updateChapterContent: (id, content) => {
        const { activeNovelId } = get();
        if (!activeNovelId) return;
        const wordCount = countWords(content);
        const updatedAt = new Date().toISOString();
        set(state => ({
          novels: state.novels.map(n => n.id === activeNovelId ? {
            ...n,
            chapters: n.chapters.map(ch => ch.id === id ? { ...ch, content, wordCount, updatedAt } : ch)
          } : n)
        }));
      },
      deleteChapter: (id) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const chapter = novel.chapters.find(ch => ch.id === id);
        if (chapter && chapter.content) {
          const compostItem: CompostItem = { id: generateId(), content: chapter.content, chapterId: id, chapterTitle: chapter.title, deletedAt: new Date().toISOString(), wordCount: chapter.wordCount };
          set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: n.chapters.filter(ch => ch.id !== id), compost: [compostItem, ...n.compost], textLinks: n.textLinks.filter(link => link.chapterId !== id), activeChapterId: n.activeChapterId === id ? (n.chapters.find(ch => ch.id !== id)?.id || null) : n.activeChapterId } : n) });
        } else {
          set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: n.chapters.filter(ch => ch.id !== id), activeChapterId: n.activeChapterId === id ? (n.chapters.find(ch => ch.id !== id)?.id || null) : n.activeChapterId } : n) });
        }
      },
      reorderChapters: (chapterIds) => set(state => {
        if (!state.activeNovelId) return state;
        return {
          novels: state.novels.map(n => {
            if (n.id !== state.activeNovelId) return n;
            const chapterMap = new Map(n.chapters.map(ch => [ch.id, ch]));
            const reordered = chapterIds
              .map((id, index) => {
                const ch = chapterMap.get(id);
                return ch ? { ...ch, order: index } : null;
              })
              .filter((ch): ch is Chapter => ch !== null);
            return { ...n, chapters: reordered };
          })
        };
      }),
      updateChapterTitle: (chapterId, title) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: n.chapters.map(ch => ch.id === chapterId ? { ...ch, title, updatedAt: new Date().toISOString() } : ch) } : n) });
      },
      setChapterColor: (chapterId, color) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: n.chapters.map(ch => ch.id === chapterId ? { ...ch, color } : ch) } : n) });
      },
      setNovelCoverColor: (novelId, color) => {
        set(state => ({ novels: state.novels.map(n => n.id === novelId ? { ...n, coverColor: color } : n) }));
      },

      createBubble: (type, name, content = '') => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return '';
        const newBubble: Bubble = { id: generateId(), type, name, content, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), x: 20 + Math.random() * 60, y: 20 + Math.random() * 60, connections: [] };
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, bubbles: [...n.bubbles, newBubble] } : n), selectedBubbleId: newBubble.id });
        return newBubble.id;
      },
      updateBubble: (id, updates) => set(state => {
        if (!state.activeNovelId) return state;
        const updatedAt = new Date().toISOString();
        return {
          novels: state.novels.map(n =>
            n.id === state.activeNovelId
              ? { ...n, bubbles: n.bubbles.map(b => b.id === id ? { ...b, ...updates, updatedAt } : b) }
              : n
          )
        };
      }),
      deleteBubble: (id) => set(state => {
        if (!state.activeNovelId) return state;
        const novel = state.novels.find(n => n.id === state.activeNovelId);
        if (!novel) return state;

        const bubble = novel.bubbles.find(b => b.id === id);
        if (!bubble) return state;

        const compostContent = bubble.content
          ? `BUBBLE: ${bubble.name}\n\n${bubble.content}`
          : `BUBBLE: ${bubble.name}`;

        const compostItem: CompostItem = {
          id: generateId(),
          content: compostContent,
          chapterId: 'bubble-' + bubble.type,
          chapterTitle: `Deleted ${bubble.type}: ${bubble.name}`,
          deletedAt: new Date().toISOString(),
          wordCount: countWords(compostContent)
        };

        const novels = state.novels.map(n => {
          if (n.id !== state.activeNovelId) return n;
          return {
            ...n,
            bubbles: n.bubbles.filter(b => b.id !== id),
            textLinks: n.textLinks.filter(link => link.bubbleId !== id),
            scenes: n.scenes.map(s => {
              if (!s.linkedBubbles.includes(id)) return s;
              return { ...s, linkedBubbles: s.linkedBubbles.filter(bid => bid !== id) };
            }),
            compost: [compostItem, ...n.compost]
          };
        });

        return {
          novels,
          selectedBubbleId: state.selectedBubbleId === id ? null : state.selectedBubbleId
        };
      }),
      moveBubble: (id, x, y) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, bubbles: n.bubbles.map(b => b.id === id ? { ...b, x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) } : b) } : n) });
      },
      setSelectedBubble: (id) => set({ selectedBubbleId: id }),
      setHoveredBubble: (id) => set({ hoveredBubbleId: id }),
      setDraggedBubble: (id) => set({ draggedBubbleId: id }),
      connectBubbles: (fromId, toId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, bubbles: n.bubbles.map(b => { if (b.id === fromId && !b.connections.includes(toId)) return { ...b, connections: [...b.connections, toId] }; if (b.id === toId && !b.connections.includes(fromId)) return { ...b, connections: [...b.connections, fromId] }; return b; }) } : n) });
      },
      disconnectBubbles: (fromId, toId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, bubbles: n.bubbles.map(b => { if (b.id === fromId) return { ...b, connections: b.connections.filter(cid => cid !== toId) }; if (b.id === toId) return { ...b, connections: b.connections.filter(cid => cid !== fromId) }; return b; }) } : n) });
      },
      getConnectedBubbles: (bubbleId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return [];
        const novel = novels.find(n => n.id === activeNovelId);
        return novel?.bubbles.find(b => b.id === bubbleId)?.connections || [];
      },
      getBubbleConnectionCount: (bubbleId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return 0;
        const novel = novels.find(n => n.id === activeNovelId);
        return novel?.bubbles.find(b => b.id === bubbleId)?.connections.length || 0;
      },

      setConstellationZoom: (zoom) => set({ constellationZoom: Math.max(0.3, Math.min(3, zoom)) }),
      setConstellationOffset: (offset) => set({ constellationOffset: offset }),
      resetConstellationView: () => set({ constellationZoom: 1, constellationOffset: { x: 0, y: 0 } }),

      createTextLink: (text, chapterId, bubbleId, startIndex, endIndex) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const newLink: TextLink = { id: generateId(), text, chapterId, bubbleId, startIndex, endIndex };
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, textLinks: [...n.textLinks, newLink] } : n) });
      },
      deleteTextLink: (id) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, textLinks: n.textLinks.filter(link => link.id !== id) } : n) });
      },
      getLinksForChapter: (chapterId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return [];
        const novel = novels.find(n => n.id === activeNovelId);
        return novel?.textLinks.filter(link => link.chapterId === chapterId) || [];
      },

      createScene: (scene) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const newScene: Scene = { ...scene, id: generateId() };
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, scenes: [...n.scenes, newScene] } : n) });
      },
      updateScene: (id, updates) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, scenes: n.scenes.map(s => s.id === id ? { ...s, ...updates } : s) } : n) });
      },
      moveScene: (sceneId, targetAct, targetOrder) => set(state => {
        if (!state.activeNovelId) return state;
        return {
          novels: state.novels.map(n => {
            if (n.id !== state.activeNovelId) return n;

            const sceneIndex = n.scenes.findIndex(s => s.id === sceneId);
            if (sceneIndex === -1) return n;

            const scene = n.scenes[sceneIndex];
            const otherScenes = n.scenes.filter(s => s.id !== sceneId);

            // Re-order within target act
            const targetActScenes = otherScenes
              .filter(s => s.act === targetAct)
              .sort((a, b) => a.order - b.order);

            targetActScenes.splice(targetOrder, 0, { ...scene, act: targetAct });

            const updatedTargetScenes = targetActScenes.map((s, i) => ({ ...s, order: i }));

            // Re-order within source act if different
            let finalScenes = otherScenes.filter(s => s.act !== targetAct);
            if (scene.act !== targetAct) {
              const sourceActScenes = otherScenes
                .filter(s => s.act === scene.act)
                .sort((a, b) => a.order - b.order)
                .map((s, i) => ({ ...s, order: i }));
              finalScenes = [...finalScenes.filter(s => s.act !== scene.act), ...sourceActScenes];
            }

            return { ...n, scenes: [...finalScenes, ...updatedTargetScenes] };
          })
        };
      }),
      moveSceneToKanban: (sceneId, newStatus) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, scenes: n.scenes.map(s => s.id === sceneId ? { ...s, status: newStatus } : s) } : n) });
      },
      deleteScene: (id) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const scene = novel.scenes.find(s => s.id === id);
        if (scene) {
          // Add scene content to compost before deleting
          const compostContent = `SCENE: ${scene.title}\n\n${scene.description}\n\nDuration: ${scene.duration} minutes\nAct: ${scene.act}\nStatus: ${scene.status}`;
          const compostItem: CompostItem = {
            id: generateId(),
            content: compostContent,
            chapterId: scene.chapterId || 'scene-deleted',
            chapterTitle: `Deleted Scene: ${scene.title}`,
            deletedAt: new Date().toISOString(),
            wordCount: countWords(compostContent)
          };
          set({
            novels: novels.map(n => n.id === activeNovelId ? {
              ...n,
              scenes: n.scenes.filter(s => s.id !== id),
              compost: [compostItem, ...n.compost]
            } : n)
          });
        }
      },

      addToCompost: (content, chapterId, chapterTitle) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const compostItem: CompostItem = { id: generateId(), content, chapterId, chapterTitle, deletedAt: new Date().toISOString(), wordCount: countWords(content) };
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, compost: [compostItem, ...n.compost] } : n) });
      },
      restoreFromCompost: (id) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const item = novel.compost.find(c => c.id === id);
        if (!item) return;
        const chapter = novel.chapters.find(ch => ch.id === item.chapterId);
        if (!chapter) {
          const newChapter: Chapter = { id: item.chapterId, title: item.chapterTitle, content: item.content, wordCount: item.wordCount, status: 'brainstorm', order: novel.chapters.length, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
          set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: [...n.chapters, newChapter], compost: n.compost.filter(c => c.id !== id), activeChapterId: newChapter.id } : n) });
        } else {
          const newContent = chapter.content + '\n\n' + item.content;
          set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: n.chapters.map(ch => ch.id === item.chapterId ? { ...ch, content: newContent, wordCount: countWords(newContent), updatedAt: new Date().toISOString() } : ch), compost: n.compost.filter(c => c.id !== id) } : n) });
        }
      },
      deleteFromCompost: (id) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, compost: n.compost.filter(c => c.id !== id) } : n) });
      },

      createBackup: () => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const backup: BackupEntry = { id: `backup-${Date.now()}`, timestamp: new Date().toISOString(), novelId: activeNovelId, novelSnapshot: novel };
        set(state => ({ backups: [backup, ...state.backups.slice(0, 13)], lastBackupTime: backup.timestamp }));
      },
      restoreBackup: (id) => {
        const backup = get().backups.find(b => b.id === id);
        if (!backup) return;
        set({ novels: get().novels.map(n => n.id === backup.novelId ? backup.novelSnapshot : n) });
      },
      deleteBackup: (id) => set(state => ({ backups: state.backups.filter(b => b.id !== id) })),

      createSnapshot: (chapterId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const chapter = novel.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const snapshot: VersionSnapshot = { id: `snapshot-${Date.now()}`, timestamp: new Date().toISOString(), chapterId, content: chapter.content, wordCount: chapter.wordCount };
        set(state => ({ versionSnapshots: [snapshot, ...state.versionSnapshots.slice(0, 19)] }));
      },
      restoreSnapshot: (id) => {
        const snapshot = get().versionSnapshots.find(s => s.id === id);
        if (!snapshot) return;
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapters: n.chapters.map(ch => ch.id === snapshot.chapterId ? { ...ch, content: snapshot.content, wordCount: snapshot.wordCount, updatedAt: new Date().toISOString() } : ch) } : n) });
      },
      getSnapshotsForChapter: (chapterId) => get().versionSnapshots.filter(s => s.chapterId === chapterId),

      addChapterImage: (chapterId, dataUrl, caption = '') => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return;
        const newImage: ChapterImage = { id: generateId(), chapterId, dataUrl, caption, order: novel.chapterImages.filter(img => img.chapterId === chapterId).length };
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapterImages: [...n.chapterImages, newImage] } : n) });
      },
      deleteChapterImage: (id) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapterImages: n.chapterImages.filter(img => img.id !== id) } : n) });
      },
      reorderChapterImages: (chapterId, imageIds) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return;
        set({ novels: novels.map(n => n.id === activeNovelId ? { ...n, chapterImages: n.chapterImages.map(img => img.chapterId === chapterId ? { ...img, order: imageIds.indexOf(img.id) } : img) } : n) });
      },

      startWritingSession: () => {
        const newSession: WritingSession = { id: `session-${Date.now()}`, startTime: new Date().toISOString(), endTime: null, duration: 0, wordCount: get().novels.find(n => n.id === get().activeNovelId)?.chapters.reduce((sum, ch) => sum + ch.wordCount, 0) || 0 };
        set(state => ({ sessions: [...state.sessions, newSession] }));
      },
      endWritingSession: () => {
        const activeSession = get().sessions.find(s => s.endTime === null);
        if (!activeSession) return;
        const endTime = new Date();
        const startTime = new Date(activeSession.startTime);
        const duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
        set(state => ({ sessions: state.sessions.map(s => s.id === activeSession.id ? { ...s, endTime: endTime.toISOString(), duration } : s) }));
      },
      incrementWritingTime: (seconds) => set(state => ({ todayWritingTime: state.todayWritingTime + seconds })),
      updateStreak: () => {
        const today = new Date().toISOString().split('T')[0];
        const { streak } = get();
        if (streak.lastWritingDate === today) return;
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        const newStreak = streak.lastWritingDate === yesterdayStr ? streak.currentStreak + 1 : 1;
        set({ streak: { currentStreak: newStreak, lastWritingDate: today, totalDaysWritten: streak.totalDaysWritten + 1, isPaused: false } });
      },
      startFocusSession: (minutes) => set({ focusSession: { isActive: true, startTime: new Date().toISOString(), duration: minutes, timeRemaining: minutes * 60 } }),
      endFocusSession: () => set({ focusSession: { isActive: false, startTime: null, duration: 0, timeRemaining: 0 } }),
      updateFocusTimeRemaining: (seconds) => set(state => ({ focusSession: { ...state.focusSession, timeRemaining: seconds } })),
      updateMomentum: (level) => set({ momentum: { level, lastTypingTime: new Date().toISOString() } }),
      setLastWarmStart: (timestamp) => set({ lastWarmStart: timestamp }),

      recordDailyWords: (wordsAdded) => {
        if (wordsAdded <= 0) return;
        const today = new Date().toISOString().split('T')[0];
        set(state => ({
          dailyWordCounts: {
            ...state.dailyWordCounts,
            [today]: (state.dailyWordCounts[today] ?? 0) + wordsAdded,
          },
        }));
        // Also update streak whenever words are recorded
        get().updateStreak();
      },

      exportToMarkdown: () => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return '';
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return '';
        let markdown = `# ${novel.title}\n\n`;
        if (novel.description) markdown += `> ${novel.description}\n\n`;
        novel.chapters.sort((a, b) => a.order - b.order).forEach(chapter => {
          markdown += `## ${chapter.title}\n\n${chapter.content}\n\n`;
        });
        return markdown;
      },
      exportToTxt: () => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return '';
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return '';
        return novel.chapters.sort((a, b) => a.order - b.order).map(ch => `${ch.title}\n\n${ch.content}`).join('\n\n---\n\n');
      },
      exportChapterToTxt: (chapterId) => {
        const { activeNovelId, novels } = get();
        if (!activeNovelId) return '';
        const novel = novels.find(n => n.id === activeNovelId);
        if (!novel) return '';
        const chapter = novel.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return '';
        return `${chapter.title}\n\n${chapter.content}`;
      },
    }), {
    name: 'authors-nexus-storage',
    partialize: (state) => ({
      // Persist only non-transient state
      license: state.license,
      themeProfile: state.themeProfile,
      previousThemePreset: state.previousThemePreset,
      hasCompletedOnboarding: state.hasCompletedOnboarding,
      hasSeenConstellationTour: state.hasSeenConstellationTour,
      settings: state.settings,
      sidebarMode: state.sidebarMode,
      viewMode: state.viewMode,
      showBubblePanel: state.showBubblePanel,
      timelineMode: state.timelineMode,
      showSettings: state.showSettings,
      theme: state.theme,
      novels: state.novels,
      activeNovelId: state.activeNovelId,
      selectedBubbleId: state.selectedBubbleId,
      backups: state.backups,
      lastBackupTime: state.lastBackupTime,
      versionSnapshots: state.versionSnapshots,
      todayWritingTime: state.todayWritingTime,
      sessions: state.sessions,
      streak: state.streak,
      focusSession: state.focusSession,
      momentum: state.momentum,
      lastWarmStart: state.lastWarmStart,
      dailyWordCounts: state.dailyWordCounts,
      lastSyncFailed: state.lastSyncFailed,
      // Explicitly exclude transient UI state:
      // - constellationZoom
      // - constellationOffset  
      // - draggedBubbleId
      // - hoveredBubbleId
      // - isSyncing
      // - syncError
    }) as any,
  }
  )
);




// themePresets, defaultThemeProfile, defaultSettings, coverColors already exported above
